import { motion } from "framer-motion";
import { TestTube, ArrowRight, BarChart2, LineChart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

export const CROHero = () => {
  return (
    <section className="relative min-h-[80vh] flex items-center overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-950/95 via-blue-900/95 to-blue-800/90" />
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1487058792275-0ad4aaf24ca7?q=80')`,
        }}
      />
      <div className="max-w-7xl mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-white space-y-6"
          >
            <div className="flex items-center gap-3 text-blue-300">
              <TestTube className="h-6 w-6" />
              <span className="text-lg font-medium">CRO & Testing Services</span>
            </div>
            <h1 className="text-5xl font-bold leading-tight">
              Transform Your <span className="text-blue-300">Conversion Rates</span> Through Scientific Testing
            </h1>
            <p className="text-xl text-blue-100">
              Unlock your website's full potential with data-driven optimization 
              and rigorous A/B testing. We turn visitors into customers through 
              systematic experimentation and continuous improvement.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link to="/contact">
                <Button size="lg" className="bg-blue-500 hover:bg-blue-600 text-white w-full sm:w-auto">
                  Start Optimizing
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/case-studies">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="bg-black/20 border-2 border-white text-white hover:bg-white/20 backdrop-blur-sm w-full sm:w-auto"
                >
                  View Case Studies
                </Button>
              </Link>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:block hidden"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-blue-500/20 rounded-3xl transform rotate-3" />
              <div className="relative bg-white/95 backdrop-blur-sm p-8 rounded-3xl border border-blue-400/20 shadow-xl">
                <div className="space-y-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="space-y-1">
                      <h3 className="font-semibold text-gray-800">Conversion Analytics</h3>
                      <p className="text-sm text-gray-500">Last 30 days performance</p>
                    </div>
                    <div className="flex gap-2">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <BarChart2 className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <LineChart className="w-4 h-4 text-blue-600" />
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { label: 'Conversion Rate', value: '4.28%', trend: '+0.6%' },
                      { label: 'Avg. Session', value: '2:45', trend: '+0.8%' },
                      { label: 'Bounce Rate', value: '33.9%', trend: '-2.1%' },
                      { label: 'Active Users', value: '1,249', trend: '+12%' }
                    ].map((stat, i) => (
                      <div key={i} className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-500 mb-1">{stat.label}</p>
                        <div className="flex items-end justify-between">
                          <span className="text-xl font-semibold text-gray-800">{stat.value}</span>
                          <span className={`text-sm ${stat.trend.startsWith('+') ? 'text-green-500' : 'text-red-500'}`}>
                            {stat.trend}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-gray-50 p-6 rounded-xl">
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-600">Homepage</span>
                        <span className="text-sm text-green-500">5.2%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{ width: '75%' }} />
                      </div>
                      
                      <div className="flex justify-between items-center mt-4">
                        <span className="text-sm font-medium text-gray-600">Product Page</span>
                        <span className="text-sm text-blue-500">3.8%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-blue-500 h-2 rounded-full" style={{ width: '60%' }} />
                      </div>
                      
                      <div className="flex justify-between items-center mt-4">
                        <span className="text-sm font-medium text-gray-600">Checkout</span>
                        <span className="text-sm text-orange-500">2.9%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-orange-500 h-2 rounded-full" style={{ width: '45%' }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};